import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { EngineSource, EntitySuggestion } from '@/types';
import { ENGINE_COLOR_CONFIG } from '@/types';

interface TesterModeContextValue {
  isTesterMode: boolean;
  enabledEngines: Set<EngineSource>;
  toggleEngine: (engine: EngineSource) => void;
  filterSuggestions: (suggestions: EntitySuggestion[]) => EntitySuggestion[];
}

const ALL_ENGINES = new Set(Object.keys(ENGINE_COLOR_CONFIG) as EngineSource[]);

const TesterModeContext = createContext<TesterModeContextValue>({
  isTesterMode: false,
  enabledEngines: ALL_ENGINES,
  toggleEngine: () => {},
  filterSuggestions: (s) => s,
});

export function TesterModeProvider({ children }: { children: ReactNode }) {
  const isTesterMode = typeof window !== 'undefined' && localStorage.getItem('testerMode') === 'true';
  const [enabledEngines, setEnabledEngines] = useState<Set<EngineSource>>(ALL_ENGINES);

  const toggleEngine = useCallback((engine: EngineSource) => {
    setEnabledEngines((prev) => {
      const next = new Set(prev);
      if (next.has(engine)) {
        next.delete(engine);
      } else {
        next.add(engine);
      }
      return next;
    });
  }, []);

  const filterSuggestions = useCallback(
    (suggestions: EntitySuggestion[]) => {
      if (!isTesterMode) return suggestions;
      return suggestions.filter((s) => enabledEngines.has((s.source || 'ai') as EngineSource));
    },
    [isTesterMode, enabledEngines],
  );

  const value = useMemo(
    () => ({ isTesterMode, enabledEngines, toggleEngine, filterSuggestions }),
    [isTesterMode, enabledEngines, toggleEngine, filterSuggestions],
  );

  return <TesterModeContext.Provider value={value}>{children}</TesterModeContext.Provider>;
}

export function useTesterMode() {
  return useContext(TesterModeContext);
}
